import { analyticsOptOutEnabled } from "./analytics-privacy";
import { CAMPAIGN_FIELDS, campaignEventProperties, clearCampaignAttribution, entryCampaignParameters } from "./campaign-attribution";

const MEASUREMENT_ID = "G-F3WF0QPBED";
const PRODUCTION_HOST = "console.limrun.com";
const SIGNUP_MARKER_PREFIX = "analytics:ga4SignupSent:";
type GoogleWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
let initialized = false;
let identifiedUserTid: string | null = null;
let identifiedUserIsInternal = false;

function captureAllowed(): boolean {
  const optedOut = analyticsOptOutEnabled();
  if (optedOut) clearCampaignAttribution();
  // The documented disable flag also stops automatic Google events after a changed privacy choice.
  (window as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] = optedOut;
  return window.location.hostname === PRODUCTION_HOST && !optedOut;
}

export function refreshGoogleAnalyticsPrivacy(): void {
  captureAllowed();
}

export function googlePageContext(): Record<string, string> {
  // Routes outside these public navigation surfaces can contain customer resource identifiers.
  const route = ["/", "/stream", "/authn/login", "/onboarding"].includes(window.location.pathname)
    ? window.location.pathname : "/console";
  const page = new URL(route, `https://${PRODUCTION_HOST}`);
  for (const [key, value] of Object.entries(entryCampaignParameters())) page.searchParams.set(key, value);
  let referrer = "";
  try {
    const url = new URL(document.referrer);
    if (url.protocol === "https:" || url.protocol === "http:") referrer = url.origin + "/";
  } catch { /* Direct visits have no referrer. */ }
  return { page_location: page.toString(), page_referrer: referrer, page_title: "Limrun Console" };
}

export function initializeGoogleAnalytics(): void {
  if (initialized || !captureAllowed()) return;
  // Callback pages carry authentication credentials and do not need a separate measurement load.
  if (window.location.pathname.startsWith("/authn/") && window.location.pathname !== "/authn/login") return;
  const target = window as GoogleWindow;
  target.dataLayer ??= [];
  target.gtag ??= function () { target.dataLayer!.push(arguments); };
  const gtag = target.gtag;
  // Preserve all existing configured cross-domain destinations when specifying the linker explicitly.
  gtag("set", "linker", { domains: ["limrun.com", "lim.run", "cal.com"], accept_incoming: true });
  gtag("js", new Date());
  const campaign = entryCampaignParameters();
  const campaignConfig: Record<string, string> = {};
  for (const [utm, config] of CAMPAIGN_FIELDS) {
    if (campaign[utm] && config.startsWith("campaign_")) campaignConfig[config] = campaign[utm];
  }
  gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    cookie_domain: "auto",
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...googlePageContext(),
    ...campaignConfig,
  });
  initialized = true;
  captureGoogleEvent("page_view");
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  script.referrerPolicy = "origin";
  document.head.appendChild(script);
}

export function identifyGoogleAnalyticsUser(userTid: string | null, isInternalOrTest = false): void {
  if (userTid === null) {
    identifiedUserTid = null;
    identifiedUserIsInternal = false;
  }
  if (!initialized || !captureAllowed()) return;
  if (userTid !== null && (!userTid.startsWith("user_") || userTid.length <= 5 || userTid.length > 105 || /[^a-z0-9_]/i.test(userTid))) return;
  identifiedUserIsInternal = isInternalOrTest || (userTid !== null && userTid === identifiedUserTid && identifiedUserIsInternal);
  identifiedUserTid = userTid;
  (window as GoogleWindow).gtag?.("config", MEASUREMENT_ID, {
    send_page_view: false,
    user_id: identifiedUserIsInternal ? null : userTid,
    traffic_type: identifiedUserIsInternal ? "internal" : "external",
    ...googlePageContext(),
  });
}

export function captureGoogleEvent(event: "page_view" | "auth_started" | "auth_completed", properties: Record<string, string> = {}): void {
  sendGoogleEvent(event, properties);
}

// The caller consumes the backend's new-account return only after whoami confirms this user's session.
export function captureGoogleSignup(provider: string): void {
  if (!initialized || !captureAllowed() || !identifiedUserTid || identifiedUserIsInternal) return;
  if (typeof (window as GoogleWindow).gtag !== "function") return;
  try {
    const marker = `${SIGNUP_MARKER_PREFIX}${identifiedUserTid}`;
    if (localStorage.getItem(marker)) return;
    // Claim before queuing to avoid normal callback replays and repeated React effects in this browser.
    localStorage.setItem(marker, "1");
  } catch {
    // Do not risk repeated signup counts when browser repeat protection is unavailable.
    return;
  }
  const method: Record<string, string> = ["google", "github", "email", "sso"].includes(provider) ? { method: provider } : {};
  sendGoogleEvent("sign_up", method);
}

function sendGoogleEvent(event: "page_view" | "auth_started" | "auth_completed" | "sign_up", properties: Record<string, string> = {}): void {
  if (!initialized || !captureAllowed()) return;
  // Confirmed bookings remain owned by the existing Cal sender.
  const attribution = campaignEventProperties();
  const manualAttribution: Record<string, string> = {};
  for (const [key, value] of Object.entries(attribution)) {
    if (key.startsWith("utm_") || key.startsWith("first_utm_")) manualAttribution[`acquisition_${key}`] = value;
  }
  (window as GoogleWindow).gtag?.("event", event, {
    send_to: MEASUREMENT_ID,
    ...googlePageContext(),
    ...manualAttribution,
    ...properties,
  });
}
